/**
 * Shopify 幸运转盘促销模块 - JavaScript功能
 * 版本: 1.0.0
 * 描述: 纯前端展示模块，支持响应式布局和图片懒加载
 */

class WheelPromotionModule {
  constructor() {
    this.modules = [];
    this.init();
  }

  init() {
    this.findModules();
    this.setupModules();
    this.setupGlobalEvents();
  }

  findModules() {
    const moduleElements = document.querySelectorAll('.wheel-promotion-module');
    moduleElements.forEach(element => {
      this.modules.push(new WheelModule(element));
    });
  }

  setupModules() {
    this.modules.forEach(module => {
      module.init();
    });
  }

  setupGlobalEvents() {
    // 处理Shopify主题编辑器的实时预览
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        const element = event.target;
        if (element.classList.contains('wheel-promotion-module')) {
          const module = new WheelModule(element);
          module.init();
          this.modules.push(module);
        }
      });

      document.addEventListener('shopify:section:unload', (event) => {
        const element = event.target;
        const moduleIndex = this.modules.findIndex(m => m.element === element);
        if (moduleIndex !== -1) {
          this.modules[moduleIndex].destroy();
          this.modules.splice(moduleIndex, 1);
        }
      });
    }
  }
}

class WheelModule {
  constructor(element) {
    this.element = element;
    this.gridItems = [];
    this.isMobile = false;
    this.imageObserver = null;
    this.resizeObserver = null;
  }

  init() {
    this.setupElements();
    this.setupImageLazyLoading();
    this.setupResponsive();
    this.setupAccessibility();
    this.animateOnScroll();
  }

  setupElements() {
    this.gridContainer = this.element.querySelector('.wheel-grid');
    this.gridItems = this.element.querySelectorAll('.wheel-grid-item');
    this.wheelImage = this.element.querySelector('.wheel-image');
    this.couponTags = this.element.querySelectorAll('.coupon-tag');
  }

  setupImageLazyLoading() {
    // 使用Intersection Observer实现图片懒加载
    if ('IntersectionObserver' in window) {
      this.imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // 观察所有图片
      const images = this.element.querySelectorAll('img[data-src]');
      images.forEach(img => {
        this.imageObserver.observe(img);
      });
    } else {
      // 降级处理：直接加载所有图片
      const images = this.element.querySelectorAll('img[data-src]');
      images.forEach(img => this.loadImage(img));
    }
  }

  loadImage(img) {
    const src = img.dataset.src;
    if (src) {
      img.src = src;
      img.classList.add('loading');
      
      img.onload = () => {
        img.classList.remove('loading');
        img.classList.add('loaded');
      };

      img.onerror = () => {
        img.classList.remove('loading');
        img.classList.add('error');
        console.warn('图片加载失败:', src);
      };
    }
  }

  setupResponsive() {
    this.checkMobile();
    
    // 使用ResizeObserver监听尺寸变化（如果支持）
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkMobile();
        this.adjustLayout();
      });
      this.resizeObserver.observe(this.element);
    } else {
      // 降级处理：使用window resize事件
      window.addEventListener('resize', this.debounce(() => {
        this.checkMobile();
        this.adjustLayout();
      }, 250));
    }
  }

  checkMobile() {
    const width = window.innerWidth;
    const wasMobile = this.isMobile;
    this.isMobile = width <= 768;
    
    if (wasMobile !== this.isMobile) {
      this.element.classList.toggle('mobile-layout', this.isMobile);
      this.element.classList.toggle('desktop-layout', !this.isMobile);
    }
  }

  adjustLayout() {
    // 调整转盘图片大小以保持比例
    if (this.wheelImage) {
      const container = this.wheelImage.parentElement;
      const containerHeight = container.offsetHeight;
      const containerWidth = container.offsetWidth;
      const size = Math.min(containerHeight * 0.8, containerWidth * 0.8, 280);
      
      this.wheelImage.style.width = size + 'px';
      this.wheelImage.style.height = size + 'px';
    }

    // 不再动态调整字体大小，让CSS控制
    // 字体大小现在由Shopify设置控制
  }

  setupAccessibility() {
    // 添加ARIA标签
    this.element.setAttribute('role', 'region');
    this.element.setAttribute('aria-label', '促销转盘模块');
    
    // 为图片添加alt文本
    this.gridItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (img && !img.hasAttribute('alt')) {
        const text = item.querySelector('.grid-item-text');
        img.setAttribute('alt', text ? text.textContent : `商品图片 ${index + 1}`);
      }
    });

    // 为优惠券标签添加描述
    this.couponTags.forEach(tag => {
      tag.setAttribute('role', 'status');
      tag.setAttribute('aria-live', 'polite');
    });

    // 键盘导航支持
    this.setupKeyboardNavigation();
  }

  setupKeyboardNavigation() {
    this.gridItems.forEach(item => {
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
      
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleItemClick(item);
        }
      });

      item.addEventListener('click', () => {
        this.handleItemClick(item);
      });
    });
  }

  handleItemClick(item) {
    // 添加点击效果
    item.classList.add('clicked');
    setTimeout(() => {
      item.classList.remove('clicked');
    }, 200);

    // 触发自定义事件
    const event = new CustomEvent('wheelItemClicked', {
      detail: { item: item },
      bubbles: true
    });
    this.element.dispatchEvent(event);
  }

  animateOnScroll() {
    // 使用Intersection Observer实现滚动动画
    if ('IntersectionObserver' in window) {
      const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            animationObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      // 观察网格项
      this.gridItems.forEach(item => {
        animationObserver.observe(item);
      });

      // 观察转盘区域
      if (this.wheelImage) {
        animationObserver.observe(this.wheelImage);
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    // 清理事件监听器
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // 清理自定义事件
    this.element.removeEventListener('wheelItemClicked', this.handleItemClick);
  }
}

// 工具函数
const Utils = {
  // 防抖函数
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  // 节流函数
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // 检测设备类型
  isMobile() {
    return window.innerWidth <= 768;
  },

  // 检测触摸支持
  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },

  // 获取元素在视口中的位置
  getElementPosition(element) {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      bottom: rect.bottom + window.scrollY,
      right: rect.right + window.scrollX
    };
  }
};

// 初始化模块
document.addEventListener('DOMContentLoaded', () => {
  // 检查是否支持所需的功能
  if (!document.querySelector || !window.addEventListener) {
    console.warn('浏览器不支持所需功能，模块初始化失败');
    return;
  }

  // 初始化主模块
  window.wheelPromotionModule = new WheelPromotionModule();
});

// 导出供其他脚本使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    WheelPromotionModule,
    WheelModule,
    Utils
  };
}